// Implements: F-PRV-009
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import styles from "./ConsolePanel.module.css";

/** One relayed line; the shape of the relay hook's message, restated because a component does not import a hook. */
export interface ConsoleLine {
  readonly kind: string;
  readonly level: string;
  readonly text: string;
}

export interface ConsolePanelProps {
  readonly messages: readonly ConsoleLine[];
}

/**
 * What the frame reported. The text comes from a document the model wrote, so it is rendered as a
 * React text child and nowhere else: never as markup.
 */
export function ConsolePanel({ messages }: ConsolePanelProps): ReactElement {
  const { t } = useTranslation();
  return (
    <section className={styles.panel} aria-label={t("preview.console.title")}>
      <h3 className={styles.title}>{t("preview.console.title")}</h3>
      {messages.length === 0 ? (
        <p className={styles.empty}>{t("preview.console.empty")}</p>
      ) : (
        <ul className={styles.list}>
          {messages.map((message, index) => (
            // The list only grows within one load of the frame, so the position is its identity.
            <li key={index} className={styles.item} data-level={message.level}>
              <span className={styles.level}>
                {message.level === "warn"
                  ? t("preview.console.level.warn")
                  : t("preview.console.level.error")}
              </span>{" "}
              <span className={styles.kind}>{message.kind}</span>{" "}
              <span className={styles.text}>{message.text}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
